import { ZagentAgentType } from '@zerops/zerops/core/zerops-services';

export interface AgentAuthHandler {
  /** Human-readable name shown in dialog title */
  displayName: string;

  /** Command to send to terminal to start OAuth login */
  loginCommand: string;

  /**
   * Direction of the OAuth code exchange — drives the dialog UX:
   *
   * - `paste-code` (Claude): browser → terminal. User opens URL, browser
   *   shows a code, user pastes that code back into the dialog's form
   *   which submits it to the terminal. Dialog auto-opens the URL.
   *
   * - `device-code` (Codex): terminal → browser. Terminal prints a device
   *   code, user opens the URL in their browser and types that code there.
   *   CLI polls until success. Dialog displays the device code prominently
   *   with a Copy button and a separate Open button. No auto-open.
   *
   * - `url-poll` (Cursor): terminal → browser, but with NO code in either
   *   direction. Terminal prints a single URL (challenge + uuid embedded),
   *   the CLI self-polls until the user approves the sign-in in their
   *   browser. Dialog auto-opens the URL (like paste-code) but renders the
   *   device-code "waiting…" card WITHOUT a code box or a paste form — the
   *   user just approves and we complete on `authSuccessPattern`.
   */
  flowMode: 'paste-code' | 'device-code' | 'url-poll';

  /**
   * Regex to detect Y/N confirmation prompts (auto-confirmed with 'y\n').
   * Only needed for prompts that require a specific answer — generic
   * "press enter" / TUI menu screens are handled by the stall timer.
   */
  confirmPromptPattern: RegExp;

  /**
   * Regex to extract the auth URL from terminal output. Matched first
   * against complete OSC 8 hyperlink targets, then against
   * terminator-gated plain text — see `zcp-agent-auth-dialog.output-parser.ts`.
   *
   * A URL-PREFIX ANCHOR, not a full pattern — the output parser appends the
   * URL token tail itself and gates completion on that same charset, so
   * handlers must NOT add their own trailing wildcard (no `[^\s]*` etc.).
   */
  authUrlPattern: RegExp;

  /**
   * Regex to extract the device code from terminal output. Required when
   * `flowMode === 'device-code'`. The first capture group (`match[1]`) is
   * preferred; falls back to `match[0]` if the pattern has no group.
   */
  deviceCodePattern?: RegExp;

  /** Regex to detect successful authentication */
  authSuccessPattern: RegExp;

  /** Regex to detect authentication errors */
  authErrorPattern: RegExp;

  /** Regex to detect "paste code here" prompt — stops stall timer, triggers code input phase */
  pasteCodePattern: RegExp;

  /**
   * If true, the 1s stall timer also fires during the `submitting_code`
   * phase (after the user pastes the auth code) — auto-pressing Enter
   * through multi-screen first-run setup that some agents show post-auth
   * (theme pickers, ToS consent, welcome banners). Default false — most
   * agents go straight from "code accepted" to the REPL.
   *
   * When enabled, `authSuccessPattern` should match the FINAL ready state
   * (post-setup REPL prompt) rather than an early welcome banner, so the
   * stall has the room it needs to navigate past the setup screens before
   * success fires. Pairing a too-early success pattern with this flag
   * defeats the auto-navigation — success fires first, walker advances,
   * setup screens never get the stall's Enter presses.
   *
   * Use `postAuthScreens` instead when the setup screens need non-Enter
   * key sequences (arrow keys, Tab, etc.).
   */
  stallDuringSubmission?: boolean;

  /**
   * Optional screen-detection-based post-auth navigation. After the user
   * submits the auth code (`submitting_code` phase) the walker iterates
   * unfired entries; the first whose `detect` regex matches the cleaned
   * output buffer has its `sendKeys` sent to the terminal. Each entry
   * fires at most once per agent run — re-runs (Retry) reset the tracker.
   *
   * Use this for CLIs whose first-run setup needs specific key sequences
   * the stall timer can't produce (arrow keys to navigate from a checkbox
   * to a Done button, Tab between focusable widgets, etc.).
   *
   * Entry order matters — earlier entries are checked first. If a later
   * screen's detect could also match earlier output, that's a bug in the
   * detect regexes, not the walker.
   *
   * `authSuccessPattern` still wins — if it matches before all screens
   * fire, the walker advances. Post-auth screens are best-effort assists,
   * not blocking gates.
   */
  postAuthScreens?: Array<{
    /** Regex to detect the screen from ANSI-stripped output */
    detect: RegExp;
    /**
     * Raw bytes to send when the screen is detected. Two forms:
     * - `string` — sent in one terminal write (good for single Enter, single Tab)
     * - `string[]` — sent as a sequence with `sendKeyDelayMs` between each
     *   item, so the TUI has time to re-render between key events. Use this
     *   for multi-key sequences (Tab Tab Enter / Right Right Enter) — bursts
     *   sent in a single write are sometimes processed against stale focus
     *   state mid-render.
     */
    sendKeys: string | string[];
    /**
     * Delay (ms) between items when `sendKeys` is an array. Default 100ms —
     * empirically enough for Ink/Bubble-Tea TUIs to render between events.
     */
    sendKeyDelayMs?: number;
  }>;
}

/**
 * Registry of supported agent types for post-deploy OAuth.
 *
 * Design philosophy:
 * - The stall timer (1s) is the PRIMARY mechanism for navigating unknown TUI screens (sends enter → selects default).
 * - Patterns exist ONLY for things that need a non-default response:
 *   confirmPromptPattern → needs 'y\n' (not just enter)
 *   authUrlPattern → needs URL extraction
 *   pasteCodePattern → must STOP auto-pressing enter
 *   authSuccessPattern / authErrorPattern → phase transitions
 * - Extracted-value patterns (`authUrlPattern`, `deviceCodePattern`) are
 *   completion-gated by the output parser (anchor + appended token tail).
 * - Presence patterns (`confirmPromptPattern`, `authSuccessPattern`,
 *   `authErrorPattern`, `pasteCodePattern`, `postAuthScreens[].detect`) test
 *   the cleaned text directly and must use `\s+` (never a literal space)
 *   between words — the parser injects `\v` boundaries at cursor rewrites
 *   and `\v` is inside `\s`.
 */
export const AGENT_AUTH_HANDLERS: Partial<Record<ZagentAgentType, AgentAuthHandler>> = {
  'claude-code': {
    displayName: 'Claude Code',
    loginCommand: 'claude /login\n',
    flowMode: 'paste-code',
    confirmPromptPattern: /\(y\/n\)/i,
    authUrlPattern: /https:\/\/[^\s]*\/oauth\/authorize/,
    authSuccessPattern: /login\s*successful|successfully\s+(authenticated|logged\s*in|authorized)/i,
    authErrorPattern: /(?:authentication|oauth)\s*error|invalid.*(?:authorization\s*)?code|login\s*failed|auth(?:entication|orization)?\s*failed/i,
    pasteCodePattern: /paste\s*code\s*here/i,
  },

  // Phase 3 — Codex device-auth flow. UNLIKE Claude (browser → terminal
  // paste-code), Codex device-auth is the reverse: terminal prints a code,
  // user enters it into the browser, CLI polls until success. The walker
  // detects the URL + opens it in a new tab; the user reads the code off
  // the terminal pane and types it into their browser. CLI polls and
  // auto-completes — we detect that via `authSuccessPattern`.
  //
  // `confirmPromptPattern` + `pasteCodePattern` are never-match because the
  // device-auth flow has no y/n prompts and no terminal-side paste field.
  // The dialog's "paste code here" form is still rendered today (it's
  // hardcoded to the `waiting_for_code` phase) — that's a UX follow-up to
  // branch on handler flow-mode. For now the user ignores that field and
  // types the code into their browser; the walker auto-completes on the
  // success message regardless.
  //
  // IMPORTANT: localhost-callback flow (`codex login` without `--device-auth`)
  // spins up `localhost:1455` inside the container, which the user's browser
  // can't reach. Device-auth is the only viable flow for remote containers.
  'codex': {
    displayName: 'Codex',
    loginCommand: 'codex login --device-auth\n',
    flowMode: 'device-code',
    confirmPromptPattern: /(?!)/,
    authUrlPattern: /https:\/\/auth\.openai\.com\/codex\/device/,
    // Codex device codes are printed as `XXXX-XXXXX` (uppercase alphanumeric
    // separated by a single dash). Captured group is the code itself; the
    // surrounding whitespace anchors guard against false matches in URLs.
    deviceCodePattern: /\b([A-Z0-9]{4}-[A-Z0-9]{5})\b/,
    authSuccessPattern: /successfully\s+logged\s+in/i,
    authErrorPattern: /authentication\s+failed|authorization\s+(?:failed|denied|expired)|invalid\s+code|device\s+code\s+expired/i,
    pasteCodePattern: /(?!)/,
  },

  // Phase 4 — Antigravity (`agy`). Tuned from live capture. Standard
  // Google OAuth paste-code flow (browser → terminal): user opens the
  // Google consent URL, signs in, copies the auth code from the callback
  // page, and pastes it back into the dialog form.
  //
  // Notable quirks the walker handles:
  // - The CLI launches with a login-method picker (Google OAuth vs Google
  //   Cloud Project, arrow-key + Enter). Walker has no explicit handling —
  //   the 1s stall timer auto-Enters through, which accepts the default
  //   `> 1. Google OAuth`.
  // - The CLI emits the full OAuth URL as the target of a standard OSC 8
  //   terminal hyperlink, once per rendered row (all rows share the same
  //   link id, which is why terminals render it as a single clickable
  //   link). The visible row text is only a hard-wrapped SLICE of the URL
  //   — at narrow widths it wraps across ~9 lines — but the output parser
  //   reads the complete link target directly off the OSC 8 sequence, so
  //   the visible wrapping never matters.
  // - URL pattern is an anchor only — the output parser appends the URL
  //   token tail itself, so extraction can never diverge from the
  //   completion gate and never runs into the trailing `──────` divider
  //   after the URL.
  //
  // POST-AUTH FIRST-RUN SETUP — driven by `postAuthScreens`. After paste,
  // the CLI walks through (1) theme picker — default `> terminal` accepts
  // on Enter, (2) ToS consent — default focus is the `[x]` checkbox where
  // Enter would TOGGLE OFF; reaching `[Done]` requires right-arrow twice
  // + Enter. The walker matches each screen marker and sends the right
  // key sequence (defined inline below). Once the REPL renders we match
  // `? for shortcuts` (the help hint unique to the ready state) and the
  // walker advances.
  //
  // PRIVACY NOTE: the ToS screen's default `[x]` is OPT-IN to Google's
  // interactions data-collection. Auto-confirming Done accepts that
  // default on the user's behalf. The user explicitly requested
  // "Auto-press through with defaults" — flagged so future reviewers
  // know the trade-off.
  //
  // No subcommand: launching `agy` alone triggers the welcome + sign-in
  // flow per Google's docs.
  'antigravity': {
    displayName: 'Antigravity',
    loginCommand: 'agy\n',
    flowMode: 'paste-code',
    confirmPromptPattern: /(?!)/,
    authUrlPattern: /https:\/\/accounts\.google\.com\/o\/oauth2\/auth/,
    authSuccessPattern: /\?\s+for\s+shortcuts/i,
    authErrorPattern: /authentication\s+failed|authorization\s+(?:failed|denied|expired)|invalid.*code|login\s+failed|sign[- ]?in\s+failed/i,
    pasteCodePattern: /paste\s+the\s+authorization\s+code/i,
    postAuthScreens: [
      // Theme picker — `> terminal` is the default highlighted option,
      // Enter accepts it.
      { detect: /Choose\s+your\s+color\s+scheme/i, sendKeys: '\r' },
      // ToS — default focus is the `[x]` checkbox (defaults to checked).
      // Empirically, sending `\x1b[C\x1b[C\r` (Right Right Enter) as a
      // single burst toggles the checkbox OFF and fails to navigate —
      // either arrows aren't bound for widget focus in this form, or
      // bursting all keys at once lands the final Enter on the checkbox
      // before arrows take effect. Tab is the more universal "next
      // focusable" key in Ink/Bubble-Tea TUIs; sequenced as an array
      // with default 100ms delay so each Tab + final Enter is processed
      // against a fully-rendered frame.
      {
        detect: /Terms\s+of\s+Service\s+&\s+Data\s+Use/i,
        sendKeys: [ '\t', '\t', '\r' ],
      },
    ],
  },

  // Phase 4.5 — Grok Build. Binary is `grok` (the product is "Grok Build"
  // but the binary keeps the short name). Per xAI docs there is NO
  // `--device-auth` flag on the official CLI — that was a wrong inference
  // from Codex's pattern. Two real auth paths:
  //   - `grok login` → browser OAuth (broken in remote containers, same
  //     localhost-callback problem as default `codex login`)
  //   - `XAI_API_KEY` env var → fully headless, no OAuth flow at all
  //
  // For OAuth-mode in our container, the CLI likely falls back to a
  // paste-code-style prompt (Antigravity pattern) when it can't open the
  // browser — `flowMode: 'paste-code'` is the best guess pre-capture.
  // Patterns are placeholders pending live `grok login` capture; DO NOT
  // click Start until verified — stall timer + wrong regexes would trample
  // manual input.
  'grok': {
    displayName: 'Grok Build',
    loginCommand: 'grok login\n',
    flowMode: 'paste-code',
    confirmPromptPattern: /(?!)/,
    authUrlPattern: /https:\/\/[^\s]*(?:x\.ai|grok\.com|x\.com)/,
    authSuccessPattern: /successfully\s+(?:logged\s*in|authenticated|authorized)|login\s+successful|signed\s+in/i,
    authErrorPattern: /authentication\s+failed|authorization\s+(?:failed|denied|expired)|invalid\s+code|login\s+failed/i,
    pasteCodePattern: /(?!)/,
  },

  // Cursor CLI (`cursor-agent`). Verified against live capture (2026-07-02).
  // A THIRD flow shape — `url-poll`: the CLI prints a single sign-in URL
  // (challenge + uuid embedded, nothing to type back) and self-polls until
  // the user approves in their browser. Unlike Codex device-auth there is
  // NO code to display; unlike Claude paste-code there is nothing to paste
  // back. The dialog auto-opens the URL and waits for the success line.
  //
  // `NO_OPEN_BROWSER=1` is REQUIRED: without it the CLI tries to open a
  // browser inside the container (which has none) instead of printing the
  // URL — same class of problem as `codex login`'s localhost callback.
  //
  // Captured flow:
  //   Starting login process...
  //   Authenticating with Cursor...
  //   Waiting for browser authentication...
  //   Open a browser and navigate to this link: https://cursor.com/loginDeepControl?challenge=…&uuid=…&mode=login&redirectTarget=cli
  //   ✓ Logged in as <email>
  //   Authentication tokens stored securely.
  //
  // `confirmPromptPattern` + `pasteCodePattern` are never-match (no y/n
  // prompts, no terminal-side paste field). `authErrorPattern` is a
  // best-guess placeholder — a failure was not reproduced during capture.
  'cursor': {
    displayName: 'Cursor CLI',
    loginCommand: 'NO_OPEN_BROWSER=1 cursor-agent login\n',
    flowMode: 'url-poll',
    confirmPromptPattern: /(?!)/,
    authUrlPattern: /https:\/\/cursor\.com\/loginDeepControl\?/,
    authSuccessPattern: /logged\s+in\s+as|authentication\s+tokens\s+stored\s+securely/i,
    authErrorPattern: /authentication\s+failed|authorization\s+(?:failed|denied|expired)|invalid\s+code|login\s+failed/i,
    pasteCodePattern: /(?!)/,
  },
};

/**
 * Check if a given agent type has a supported auth handler.
 */
export function hasAuthHandler(agentType: ZagentAgentType): boolean {
  return agentType in AGENT_AUTH_HANDLERS;
}

/**
 * Get the auth handler for a given agent type.
 */
export function getAuthHandler(agentType: ZagentAgentType): AgentAuthHandler | undefined {
  return AGENT_AUTH_HANDLERS[agentType];
}
